import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Group, Paper, Stack } from '@mantine/core';
import { IconCancel as CancelIcon, IconDeviceFloppy as SaveIcon } from '@tabler/icons-react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { VisibilityActivity } from '@/module/ResearchCenter';

import { TextInputControl } from '@/core/components';

interface CriteriaProjectFreeActivityChangeFormProps {
  onChange: (freeActivity: VisibilityActivity) => void;
  onCancel: () => void;

  defaultValues: Partial<VisibilityActivity>;

  isSubmitting: boolean;
}

export const schema = z.object({
  title: z.string().min(3),
  organizer: z.string().min(3),
  period: z.string().min(3),
  id: z.number().nullish(),
});

export type FreeActivitySchema = z.infer<typeof schema>;

export function CriteriaProjectFreeActivityChangeForm({
  onChange,
  defaultValues,
  isSubmitting,
  onCancel,
}: CriteriaProjectFreeActivityChangeFormProps) {
  const { t } = useTranslation();

  const form = useForm<FreeActivitySchema>({
    defaultValues,
    resolver: zodResolver(schema),
  });

  return (
    <FormProvider {...form}>
      <Paper
        flex={1}
        mb="10px"
        p="sm"
        shadow="sm"
        component="form"
        onSubmit={form.handleSubmit(onChange)}
      >
        <Stack>
          <input
            name="id"
            type="hidden"
          />
          <TextInputControl<FreeActivitySchema>
            withAsterisk
            name="title"
            label={t('centerReport.free.activityVisibility.title')}
            isSubmitting={isSubmitting}
          />
          <Group>
            <TextInputControl<FreeActivitySchema>
              withAsterisk
              name="organizer"
              label={t('centerReport.free.activityVisibility.organizer')}
              isSubmitting={isSubmitting}
            />
            <TextInputControl<FreeActivitySchema>
              withAsterisk
              name="period"
              label={t('centerReport.free.activityVisibility.period')}
              isSubmitting={isSubmitting}
            />
          </Group>
        </Stack>
        <Group
          mt="lg"
          justify="flex-end"
        >
          {onCancel && (
            <Button
              size="sm"
              leftSection={<CancelIcon />}
              variant="outline"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              {t('button.cancel')}
            </Button>
          )}
          <Button
            size="sm"
            leftSection={<SaveIcon />}
            type="submit"
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            {t('button.save')}
          </Button>
        </Group>
      </Paper>
    </FormProvider>
  );
}
